// ============================================================
// 📄 GERADOR DE FICHA DE CARGA VIRAL (B211) — modelo gov.br/aids
// ============================================================
// Reproduz, em HTML imprimível FIEL ao formulário oficial do Ministério da
// Saúde (gov.br/aids), o "Formulário para Solicitação de Exame de Carga Viral".
// O MESMO modelo serve Hepatite B, Hepatite C e HIV — muda só o título, o
// código do procedimento (campo 35/36) e as opções clínicas (motivo do exame
// campo 38 + estágio da doença/CID campo 39). Por isso o gerador é
// PARAMETRIZÁVEL por `ConfigCargaViral`: o HBV é a 1ª instância (AI_95); HCV e
// HIV entram como ~10 linhas de config cada, sem refazer o layout.
//
// 55 campos numerados, barras de seção horizontais cinza, listas de opções
// inline. Pré-preenchido com paciente (prontuário) + solicitante (cadastro) +
// pedido (modal). 100% client-side, sem biblioteca de PDF e sem logar conteúdo
// clínico (LGPD — classe B196). Difere da GAL (que tem layout próprio do
// Sistema GAL); aqui o molde é o do gov.br/aids.

import { telefoneVigente } from "../../../shared/utils/telefoneBR";
import { Paciente } from "../../../types";
import {
  abrirPDFEmNovaJanela,
  // helpers já testados, reusados da ficha GAL (fonte única)
} from "./gerarPDF";
import { escapeHtml, formatarDataBR } from "./gerarFichaGAL";
import susLogoBase64 from "./susLogoBase64";

// ------------------------------------------------------------
// CONFIGURAÇÃO POR DOENÇA (o que difere entre HBV / HCV / HIV)
// ------------------------------------------------------------
export interface ConfigCargaViral {
  id: string; // identificador estável (ex.: "cv-hbv")
  titulo: string; // título do formulário oficial
  codigoProcedimento: string; // campo 35 (ex.: "02.13.01.020-8"); "" na Delta (sem bloco)
  nomeProcedimento: string; // campo 36; "" na Delta
  opcoesMotivo: string; // motivo do exame — lista oficial (texto inline)
  opcoesEstagioCID: string; // estágio/CID — lista oficial (texto inline)
  fonte: string; // rodapé/origem (ex.: "gov.br/aids — 08/02/2023")
  // Opções do modal (seleções), derivadas das listas oficiais:
  motivosModal: string[]; // valores selecionáveis no modal (motivo)
  estagiosModal: string[]; // valores selecionáveis no modal (estágio/CID)
  variante?: "delta" | "hiv" | "genoHcv"; // layout alternativo; ausente = padrão HBV/HCV
}

// Dados fixos do solicitante (cadastro do usuário + registro do conselho).
// Superset compatível com o solicitante da GAL (cpfProfissional é o extra HBV).
export interface SolicitanteCargaViral {
  nomeProfissional: string; // campo 3
  registroConselho: string; // campo 4 — ex.: "CRM RO 3793"
  cpfProfissional?: string; // campo 7
  cnsProfissional?: string; // (não usado no HBV, mantido por compat.)
  unidadeSaude?: string; // campo 1 / 40
  cnes?: string; // campo 2
  municipioAtendimento?: string;
  codigoIBGE?: string;
  ufAtendimento?: string;
}

// Dados específicos do pedido (modal; persistidos junto ao pedido).
export interface DadosCargaViral {
  formularioId: string; // qual config (ex.: "cv-hbv") — torna o dado autossuficiente
  exameSolicitado: string; // rótulo do exame (campo 47/descrição)
  codigoSUS?: string; // espelha config.codigoProcedimento
  dataSolicitacao: string; // dd/mm/aaaa (campo 6)
  motivoExame?: string; // campo 38
  estagioDoencaCID?: string; // campo 39
  idadeGestacional?: string; // campo 37
  dataColeta?: string; // campo 41 (opcional — laboratório pode preencher)
  materialBiologico?: string; // campo 51 (opcional)
  // Hepatite Delta — Teste Anti-HDV-Total (campo 38), exigido p/ autorizar CV-HDV
  antiHDVResultado?: string;
  antiHDVDataColeta?: string;
  antiHDVDataLiberacao?: string;
  // HIV — DNA pró-viral: tabela de evidência laboratorial com DUAS coletas de
  // CV-HIV (campos 35–42). Preenchidas pelo solicitante p/ justificar o exame.
  hivData1Coleta?: string;
  hivMetodologia1?: string;
  hivCopias1?: string;
  hivDataResultado1?: string;
  hivData2Coleta?: string;
  hivMetodologia2?: string;
  hivCopias2?: string;
  hivDataResultado2?: string;
  // Genotipagem do VHC — campos próprios do formulário oficial gov.br/aids.
  pesoPaciente?: string; // campo 14 (obrigatório só p/ criança < 12 anos)
  numeroGAL?: string; // campo 24
  genoCID?: string; // campo 39 (default B18.2)
  genoUltimaQuantData?: string; // campo 40 — data (puxada da sorologia, se houver)
  genoUltimaQuantUIML?: string; // campo 40 — UI/mL (de SorologiaViral.hcvRNA)
  genoUltimaQuantLog?: string; // campo 40 — log (sempre manual)
  genoSolicitacaoSimultanea?: string; // campo 40.1 (Sim/Não)
  genoColetaSimultanea?: string; // campo 44 (Sim/Não)
}

// ------------------------------------------------------------
// CONFIGS REGISTRADAS (por id de formulário)
// ------------------------------------------------------------
export const CONFIG_HBV: ConfigCargaViral = {
  id: "cv-hbv",
  titulo: "Formulário para Solicitação de Exame de Carga Viral do Vírus da Hepatite B",
  codigoProcedimento: "02.13.01.020-8",
  nomeProcedimento: "Identificação do vírus da hepatite B por PCR (quantitativo)",
  opcoesMotivo:
    "1. Confirmação do diagnóstico 2. Avaliar indicação de tratamento 3. Monitorar tratamento 4. Gestante portadora de hepatite B 5. Investigação de transmissão vertical",
  opcoesEstagioCID:
    "1. Hepatite B aguda (B16) 2. Hepatite B crônica sem Delta (B18.1) 3. Hepatite B crônica com Delta (B18.0)",
  fonte: "gov.br/aids — 08/02/2023",
  motivosModal: [
    "1. Confirmação do diagnóstico",
    "2. Avaliar indicação de tratamento",
    "3. Monitorar tratamento",
    "4. Gestante portadora de hepatite B",
    "5. Investigação de transmissão vertical",
  ],
  estagiosModal: [
    "1. Hepatite B aguda (B16)",
    "2. Hepatite B crônica sem Delta (B18.1)",
    "3. Hepatite B crônica com Delta (B18.0)",
  ],
};

export const CONFIG_HCV: ConfigCargaViral = {
  id: "cv-hcv",
  titulo: "Formulário para Solicitação de Exame de Carga Viral do Vírus da Hepatite C",
  codigoProcedimento: "02.02.03.108-0",
  nomeProcedimento: "Quantificação de RNA do vírus da hepatite C",
  opcoesMotivo:
    "1. Diagnóstico 2. Avaliação pós-tratamento (RVS) 3. Suspeita de reinfecção",
  opcoesEstagioCID:
    "1. Hepatite C aguda (B17.1) 2. Hepatite C crônica (B18.2)",
  fonte: "gov.br/aids — 08/02/2023",
  motivosModal: [
    "1. Diagnóstico",
    "2. Avaliação pós-tratamento (RVS)",
    "3. Suspeita de reinfecção",
  ],
  estagiosModal: [
    "1. Hepatite C aguda (B17.1)",
    "2. Hepatite C crônica (B18.2)",
  ],
};

export const CONFIG_HDV: ConfigCargaViral = {
  id: "cv-hdv",
  titulo: "Formulário para Solicitação de Exame de Carga Viral do Vírus da Hepatite D (Delta)",
  codigoProcedimento: "", // o formulário oficial da Delta não tem bloco de procedimento
  nomeProcedimento: "",
  opcoesMotivo: "1. Diagnóstico 2. Monitoramento 3. Seguimento pós-tratamento",
  opcoesEstagioCID:
    "1. Hep. aguda B c/ Delta (co-infecção) c/ coma hepático (B16.0) 2. Hep. aguda B c/ Delta (co-infecção) s/ coma hepático (B16.1) 3. Superinfecção Delta aguda de portador de hep. B (B17.0) 4. Hep. viral crônica B c/ agente Delta (B18.0)",
  fonte: "gov.br/aids — Versão 07.2024",
  motivosModal: ["1. Diagnóstico", "2. Monitoramento", "3. Seguimento pós-tratamento"],
  estagiosModal: [
    "1. Hepatite aguda B com agente Delta (co-infecção), com coma hepático (B16.0)",
    "2. Hepatite aguda B com agente Delta (co-infecção), sem coma hepático (B16.1)",
    "3. Superinfecção Delta aguda de portador de hepatite B (B17.0)",
    "4. Hepatite viral crônica B com agente Delta (B18.0)",
  ],
  variante: "delta",
};

// HIV — "Detecção do DNA pró-viral do HIV-1" (gov.br/aids 19/02/2024). Formulário
// BEM diferente do molde HBV/HCV: numeração própria (1–52), nome/CPF da mãe nos
// campos 30/31, motivo em CAIXAS DE MARCAR (campo 34) e tabela de EVIDÊNCIA
// LABORATORIAL com 2 coletas de CV-HIV (35–42). Sem estágio/CID nem idade
// gestacional. Por isso `variante: "hiv"` com corpo dedicado.
export const CONFIG_HIV: ConfigCargaViral = {
  id: "cv-hiv",
  titulo:
    "Formulário para Solicitação de Exame de Detecção do DNA pró-viral do HIV-1",
  codigoProcedimento: "02.02.03.004-0",
  nomeProcedimento: "Detecção de ácidos nucleicos do HIV-1 (qualitativo)",
  // Motivo do exame (campo 34) — caixas oficiais; "3" exige autorização do DATHI.
  opcoesMotivo:
    "1. Teste complementar após 1ª CV-HIV detectável (qualquer valor) · 2. Idem + 2ª CV-HIV < 100 cópias/mL · 3. Outro motivo (autorização prévia do DATHI/SVSA/MS)",
  opcoesEstagioCID: "", // não há estágio/CID neste formulário
  fonte: "gov.br/aids — 19/02/2024",
  motivosModal: [
    "1. Teste complementar após 1ª CV-HIV detectável (qualquer valor)",
    "2. Idem + 2ª CV-HIV inferior a 100 cópias/mL",
    "3. Outro motivo (requer autorização prévia do DATHI/SVSA/MS)",
  ],
  estagiosModal: [], // não usado no HIV
  variante: "hiv",
};

// Genotipagem do VHC — formulário próprio (gov.br/aids 08/02/2023). Não é da
// família "carga viral", mas reaproveita o mesmo motor de geração. Tem Peso
// (campo 14), Número GAL (campo 24) e a seção "Última quantificação do RNA do
// HCV" (campo 40) que é pré-preenchida com a sorologia mais recente do paciente.
export const CONFIG_GENO_HCV: ConfigCargaViral = {
  id: "geno-hcv",
  titulo:
    "Formulário para Solicitação de Exame de Genotipagem do Vírus da Hepatite C",
  codigoProcedimento: "02.02.03.021-0",
  nomeProcedimento: "Genotipagem de Vírus da Hepatite C",
  opcoesMotivo: "", // não há campo de motivo neste formulário
  opcoesEstagioCID: "",
  fonte: "gov.br/aids — 08/02/2023",
  motivosModal: [],
  estagiosModal: [],
  variante: "genoHcv",
};

const CONFIGS: Record<string, ConfigCargaViral> = {
  "cv-hbv": CONFIG_HBV,
  "cv-hcv": CONFIG_HCV,
  "cv-hdv": CONFIG_HDV,
  "cv-hiv": CONFIG_HIV,
  "geno-hcv": CONFIG_GENO_HCV,
};

// Opções do Teste Anti-HDV-Total (campo 38 da Delta) — para o modal
export const ANTI_HDV_RESULTADO_OPCOES = [
  "1. Reagente",
  "2. Não Reagente",
  "3. Não realizado",
  "4. Indeterminado",
  "5. Ignorado",
];

/** Recupera a config de carga viral por id de formulário (default: HBV). */
export function configCargaViral(id: string | undefined): ConfigCargaViral {
  return (id && CONFIGS[id]) || CONFIG_HBV;
}

// Opções compartilhadas (iguais nos três formulários da família)
export const IDADE_GESTACIONAL_OPCOES = [
  "1. 1º trimestre",
  "2. 2º trimestre",
  "3. 3º trimestre",
  "4. Ignorado",
  "5. Não se aplica",
];

// ------------------------------------------------------------
// CÉLULA DO FORMULÁRIO (grade de 12 colunas)
// ------------------------------------------------------------
interface OpcoesCelula {
  span?: number;
  opcoes?: string; // lista de opções oficiais (texto de referência)
  obrig?: boolean;
  estilo?: string; // posicionamento explícito na grade (grid-column/grid-row)
}

function celula(
  num: string,
  rotulo: string,
  valor: string,
  opts: OpcoesCelula = {}
): string {
  const { span = 1, opcoes = "", obrig = false, estilo = "" } = opts;
  const posicao = estilo || `grid-column: span ${span};`;
  const numBox = num ? `<span class="cv-num">${num}</span>` : "";
  return `
    <div class="cv-campo" style="${posicao}">
      <div class="cv-topo">
        ${numBox}<span class="cv-lbl">${rotulo}${
    obrig ? '<span class="cv-ob">*</span>' : ""
  }</span>
      </div>
      ${opcoes ? `<div class="cv-opts">${opcoes}</div>` : ""}
      <div class="cv-fill">${valor || "&nbsp;"}</div>
    </div>`;
}

const barra = (titulo: string): string =>
  `<div class="cv-barra"><span>${titulo}</span></div>`;

const linha = (campos: string): string => `<div class="cv-linha">${campos}</div>`;

// ------------------------------------------------------------
// MONTAGEM DO MIOLO (sem <html>/<style>/@page)
// ------------------------------------------------------------
// Cabeçalho + campos 1–34 (DADOS DA INSTITUIÇÃO + INFORMAÇÕES BÁSICAS). São
// IDÊNTICOS em número e conteúdo entre HBV/HCV e Hepatite Delta — por isso
// ficam num bloco compartilhado; cada formulário acrescenta sua própria cauda.
function cabecalhoEDados134(
  paciente: Paciente,
  solicitante: SolicitanteCargaViral,
  pedido: DadosCargaViral,
  config: ConfigCargaViral,
  // B289 (AI_127) — selo ICP-Brasil compacto no campo 5 (assinatura do
  // solicitante), só no PDF assinado. Sem selo = campo em branco.
  selo?: string
): string {
  const cnsPaciente = paciente.cns || paciente.hospub; // hospub = Cartão SUS legado
  const sexoTxt =
    paciente.sexo === "F"
      ? "1. Feminino"
      : paciente.sexo === "M"
      ? "2. Masculino"
      : "";
  // Para homem, gestante é seguramente "2. Não"; para mulher deixamos em branco
  // (não afirmar ausência de gravidez automaticamente).
  const gestanteTxt = paciente.sexo === "M" ? "2. Não" : "";

  return `
    <div class="cv-cab">
      <div class="cv-logo"><img src="${susLogoBase64}" alt="Logo do SUS" /></div>
      <div class="cv-titulo">${escapeHtml(config.titulo)}</div>
    </div>

    ${barra("DADOS DA INSTITUIÇÃO")}
    ${linha(
      celula("1", "Instituição solicitante (carimbo padrão):", escapeHtml(solicitante.unidadeSaude), { span: 8, obrig: true }) +
        celula("2", "CNES:", escapeHtml(solicitante.cnes), { span: 4, obrig: true })
    )}
    <div class="cv-linha cv-grid-inst">
      ${celula("3", "Nome do profissional solicitante:", escapeHtml(solicitante.nomeProfissional), { obrig: true, estilo: "grid-column: 1 / span 5; grid-row: 1;" })}
      ${celula("4", "Registro do conselho (Conselho/UF/Nº):", escapeHtml(solicitante.registroConselho), { obrig: true, estilo: "grid-column: 6 / span 4; grid-row: 1;" })}
      ${celula("6", "Data da solicitação:", escapeHtml(pedido.dataSolicitacao), { obrig: true, estilo: "grid-column: 1 / span 5; grid-row: 2;" })}
      ${celula("7", "CPF do profissional:", escapeHtml(solicitante.cpfProfissional), { obrig: true, estilo: "grid-column: 6 / span 4; grid-row: 2;" })}
      ${celula("5", "Assinatura e Carimbo:", selo || "", { obrig: true, estilo: "grid-column: 10 / span 3; grid-row: 1 / span 2;" })}
    </div>

    ${barra("INFORMAÇÕES BÁSICAS")}
    ${linha(
      celula("8", "CNS do(a) paciente:", escapeHtml(cnsPaciente), { span: 3, obrig: true }) +
        celula("10", "Nome completo do(a) usuário(a) — Oficial:", escapeHtml(paciente.nome), { span: 6, obrig: true }) +
        celula("12", "Preferência de identificação:", "", { span: 3, obrig: true, opcoes: "1. Oficial 2. Social" })
    )}
    ${linha(
      celula("9", "CPF:", escapeHtml(paciente.cpf), { span: 3, obrig: true }) +
        celula("11", "Nome social:", "", { span: 6 }) +
        celula("13", "Sexo:", sexoTxt, { span: 3, obrig: true, opcoes: "1. Feminino 2. Masculino" })
    )}
    ${linha(
      celula("14", "Data de nascimento:", formatarDataBR(paciente.dataNascimento), { span: 3, obrig: true }) +
        celula("15", "Raça/Cor:", "", { span: 4, obrig: true, opcoes: "1. Branca 2. Preta 3. Amarela 4. Parda 5. Indígena 6. Não informado 7. Ignorada" }) +
        celula("16", "Etnia:", "", { span: 2, obrig: true }) +
        celula("17", "Nome da mãe:", escapeHtml(paciente.nomeMae), { span: 3, obrig: true })
    )}
    ${linha(
      celula("18", "Nacionalidade:", "", { span: 3, obrig: true }) +
        celula("19", "Número da identidade:", escapeHtml(paciente.rg), { span: 3 }) +
        celula("20", "Logradouro:", escapeHtml(paciente.logradouro), { span: 6, obrig: true })
    )}
    ${linha(
      celula("21", "Número:", escapeHtml(paciente.numero), { span: 2, obrig: true }) +
        celula("22", "Complemento:", escapeHtml(paciente.complemento), { span: 2 }) +
        celula("23", "Bairro:", escapeHtml(paciente.bairro), { span: 2, obrig: true }) +
        celula("24", "Município:", escapeHtml(paciente.cidade), { span: 2, obrig: true }) +
        celula("25", "Cód. IBGE:", "", { span: 1 }) +
        celula("26", "UF:", escapeHtml(paciente.estado), { span: 1, obrig: true }) +
        celula("27", "CEP:", escapeHtml(paciente.cep), { span: 2, obrig: true })
    )}
    ${linha(
      celula("28", "Telefone:", escapeHtml(telefoneVigente(paciente)), { span: 3 }) +
        celula("29", "País:", "", { span: 2, obrig: true }) +
        celula("30", "Prontuário:", "", { span: 2 }) +
        celula("31", "Gestante:", gestanteTxt, { span: 2, obrig: true, opcoes: "1. Sim 2. Não" }) +
        celula("32", "Escolaridade (anos):", "", { span: 3, opcoes: "1. Nenhuma 2. 1 a 3 3. 4 a 7 4. 8 a 11 5. 12 e mais 6. Não inf. 7. Ignorado" })
    )}
    ${linha(
      celula("33", "Nome do(a) responsável (se menor de idade ou incapaz):", "", { span: 8, obrig: true }) +
        celula("34", "CPF do(a) responsável:", "", { span: 4 })
    )}`;
}

// HBV/HCV — modelo gov.br/aids padrão (procedimento + clínica + coleta + lab).
function corpoCargaViral(
  paciente: Paciente,
  solicitante: SolicitanteCargaViral,
  pedido: DadosCargaViral,
  config: ConfigCargaViral,
  selo?: string
): string {
  return `
  <div class="cv">
    ${cabecalhoEDados134(paciente, solicitante, pedido, config, selo)}

    ${linha(
      celula("35", "Código do procedimento:", escapeHtml(config.codigoProcedimento), { span: 3 }) +
        celula("36", "Nome do procedimento:", escapeHtml(config.nomeProcedimento), { span: 9 })
    )}

    ${barra("DADOS CLÍNICOS GERAIS / DETALHES DO AGRAVO")}
    ${linha(
      celula("37", "Idade gestacional:", escapeHtml(pedido.idadeGestacional), { span: 3, obrig: true, opcoes: "1. 1ºtrim 2. 2ºtrim 3. 3ºtrim 4. Ignorado 5. Não se aplica" }) +
        celula("38", "Motivo do exame:", escapeHtml(pedido.motivoExame), { span: 6, obrig: true, opcoes: config.opcoesMotivo }) +
        celula("39", "Estágio da doença (CID):", escapeHtml(pedido.estagioDoencaCID), { span: 3, obrig: true, opcoes: config.opcoesEstagioCID })
    )}

    ${barra("LOCAL DA COLETA DA AMOSTRA")}
    ${linha(
      celula("40", "Nome da instituição:", "", { span: 8, obrig: true }) +
        celula("41", "Data da coleta:", escapeHtml(pedido.dataColeta), { span: 2, obrig: true }) +
        celula("42", "Hora da coleta:", "", { span: 2, obrig: true })
    )}

    ${barra("LABORATÓRIO EXECUTOR DO TESTE")}
    ${linha(
      celula("43", "Nome da instituição:", "", { span: 6, obrig: true }) +
        celula("44", "CNES:", "", { span: 2, obrig: true }) +
        celula("45", "Data do recebimento:", "", { span: 2, obrig: true }) +
        celula("46", "Hora do recebimento:", "", { span: 2, obrig: true })
    )}

    <div class="cv-secao-band">
      <div class="cv-band"><span>CARGA VIRAL</span></div>
      <div class="cv-band-corpo">
        ${linha(
          celula("47", "Solicitação do exame:", escapeHtml(pedido.exameSolicitado), { span: 3, obrig: true }) +
            celula("48", "Identificador da amostra:", "", { span: 3, obrig: true }) +
            celula("49", "Responsável:", "", { span: 3, obrig: true }) +
            celula("50", "Data do resultado:", "", { span: 3, obrig: true })
        )}
        ${linha(
          celula("51", "Material biológico:", escapeHtml(pedido.materialBiologico), { span: 3, obrig: true }) +
            celula("52", "Volume da amostra:", "", { span: 2 }) +
            celula("53", "UI/mL:", "", { span: 2 }) +
            celula("54", "Log:", "", { span: 2 }) +
            celula("55", "Técnica:", "", { span: 3, obrig: true })
        )}
      </div>
    </div>

    <div class="cv-rodape">* Preenchimento obrigatório · ${escapeHtml(config.fonte)} · Gerado por hepato.app — confira os dados antes de entregar ao laboratório.</div>
  </div>`;
}

// Hepatite D (Delta) — variante do modelo gov.br/aids: SEM bloco de procedimento
// (35/36 do HBV), COM seção DADOS LABORATORIAIS (Anti-HDV, campo 38) e DUPLO
// bloco de coleta (coleta 39-41 + recolhimento 42-43). Numeração própria 35–56.
function corpoCargaViralDelta(
  paciente: Paciente,
  solicitante: SolicitanteCargaViral,
  pedido: DadosCargaViral,
  config: ConfigCargaViral,
  selo?: string
): string {
  return `
  <div class="cv">
    ${cabecalhoEDados134(paciente, solicitante, pedido, config, selo)}

    ${barra("DADOS CLÍNICOS GERAIS / DETALHES DO AGRAVO")}
    ${linha(
      celula("35", "Idade gestacional:", escapeHtml(pedido.idadeGestacional), { span: 3, obrig: true, opcoes: "1. 1ºtrim 2. 2ºtrim 3. 3ºtrim 4. Ignorado 5. Não se aplica" }) +
        celula("36", "Motivo do exame:", escapeHtml(pedido.motivoExame), { span: 4, obrig: true, opcoes: config.opcoesMotivo }) +
        celula("37", "Classificação Internacional de Doenças (CID):", escapeHtml(pedido.estagioDoencaCID), { span: 5, obrig: true, opcoes: config.opcoesEstagioCID })
    )}

    ${barra("DADOS LABORATORIAIS")}
    ${linha(
      celula("38", "Teste Anti-HDV-Total — Resultado:", escapeHtml(pedido.antiHDVResultado), { span: 5, obrig: true, opcoes: "Obrigatório p/ autorizar a CV-HDV · 1. Reagente 2. Não Reagente 3. Não realizado 4. Indeterminado 5. Ignorado" }) +
        celula("", "Data da coleta (Anti-HDV):", escapeHtml(pedido.antiHDVDataColeta), { span: 4 }) +
        celula("", "Data de liberação (Anti-HDV):", escapeHtml(pedido.antiHDVDataLiberacao), { span: 3 })
    )}

    ${barra("LOCAL DA COLETA DA AMOSTRA")}
    ${linha(
      celula("39", "Nome da instituição:", "", { span: 8, obrig: true }) +
        celula("40", "Data da coleta:", escapeHtml(pedido.dataColeta), { span: 2, obrig: true }) +
        celula("41", "Hora da coleta:", "", { span: 2, obrig: true })
    )}
    ${linha(
      celula("42", "Nome da instituição (recolhimento — carimbo padrão):", "", { span: 9, obrig: true }) +
        celula("43", "Data do recolhimento:", "", { span: 3, obrig: true })
    )}

    ${barra("LABORATÓRIO EXECUTOR DO TESTE")}
    ${linha(
      celula("44", "Nome da instituição:", "", { span: 6, obrig: true }) +
        celula("45", "CNES:", "", { span: 2, obrig: true }) +
        celula("46", "Data do recebimento:", "", { span: 2, obrig: true }) +
        celula("47", "Hora do recebimento:", "", { span: 2, obrig: true })
    )}

    <div class="cv-secao-band">
      <div class="cv-band"><span>CARGA VIRAL</span></div>
      <div class="cv-band-corpo">
        ${linha(
          celula("48", "Solicitação do exame:", escapeHtml(pedido.exameSolicitado), { span: 3, obrig: true }) +
            celula("49", "Identificador da amostra:", "", { span: 3, obrig: true }) +
            celula("50", "Responsável:", "", { span: 3, obrig: true }) +
            celula("51", "Data do resultado:", "", { span: 3, obrig: true })
        )}
        ${linha(
          celula("52", "Material biológico:", escapeHtml(pedido.materialBiologico), { span: 3, obrig: true }) +
            celula("53", "Volume da amostra:", "", { span: 2 }) +
            celula("54", "UI/mL:", "", { span: 2 }) +
            celula("55", "Log:", "", { span: 2 }) +
            celula("56", "Técnica:", "", { span: 3, obrig: true })
        )}
      </div>
    </div>

    <div class="cv-rodape">* Preenchimento obrigatório · ${escapeHtml(config.fonte)} · Gerado por hepato.app — confira os dados antes de entregar ao laboratório.</div>
  </div>`;
}

// ------------------------------------------------------------
// HIV — DNA pró-viral do HIV-1 (gov.br/aids 19/02/2024)
// ------------------------------------------------------------
// Formulário com numeração e blocos PRÓPRIOS (não compartilha o cabeçalho 1–34
// do HBV): mãe nos campos 30/31, sem gestante/escolaridade/responsável, motivo
// em caixas de marcar e tabela de evidência laboratorial com 2 coletas.

// Cabeçalho + informações básicas do HIV (campos 1–31), numeração própria.
function cabecalhoEDadosHIV(
  paciente: Paciente,
  solicitante: SolicitanteCargaViral,
  pedido: DadosCargaViral,
  config: ConfigCargaViral,
  // B289 (AI_127) — selo ICP-Brasil compacto no campo 5, só no PDF assinado.
  selo?: string
): string {
  const cnsPaciente = paciente.cns || paciente.hospub;
  const sexoTxt =
    paciente.sexo === "F"
      ? "1. Feminino"
      : paciente.sexo === "M"
      ? "2. Masculino"
      : "";

  return `
    <div class="cv-cab">
      <div class="cv-logo"><img src="${susLogoBase64}" alt="Logo do SUS" /></div>
      <div class="cv-titulo">${escapeHtml(config.titulo)}</div>
    </div>

    ${barra("DADOS DA INSTITUIÇÃO")}
    ${linha(
      celula("1", "Instituição solicitante (carimbo padrão):", escapeHtml(solicitante.unidadeSaude), { span: 9, obrig: true }) +
        celula("2", "CNES:", escapeHtml(solicitante.cnes), { span: 3, obrig: true })
    )}

    ${barra("DADOS DO PROFISSIONAL SOLICITANTE")}
    <div class="cv-linha cv-grid-inst">
      ${celula("3", "Nome do profissional solicitante:", escapeHtml(solicitante.nomeProfissional), { obrig: true, estilo: "grid-column: 1 / span 5; grid-row: 1;" })}
      ${celula("4", "Registro do conselho (Sigla/UF/Nº):", escapeHtml(solicitante.registroConselho), { obrig: true, estilo: "grid-column: 6 / span 4; grid-row: 1;" })}
      ${celula("6", "Data da solicitação:", escapeHtml(pedido.dataSolicitacao), { obrig: true, estilo: "grid-column: 1 / span 5; grid-row: 2;" })}
      ${celula("7", "CPF do profissional solicitante:", escapeHtml(solicitante.cpfProfissional), { obrig: true, estilo: "grid-column: 6 / span 4; grid-row: 2;" })}
      ${celula("5", "Assinatura e Carimbo:", selo || "", { obrig: true, estilo: "grid-column: 10 / span 3; grid-row: 1 / span 2;" })}
    </div>

    ${barra("INFORMAÇÕES BÁSICAS")}
    ${linha(
      celula("8", "CNS do(a) paciente:", escapeHtml(cnsPaciente), { span: 4, obrig: true }) +
        celula("10", "Nome completo do(a) paciente (Oficial):", escapeHtml(paciente.nome), { span: 5, obrig: true }) +
        celula("12", "Preferência de identificação:", "1. Oficial", { span: 3, obrig: true, opcoes: "1. Oficial 2. Social" })
    )}
    ${linha(
      celula("9", "CPF do paciente:", escapeHtml(paciente.cpf), { span: 4, obrig: true }) +
        celula("11", "Nome social:", "", { span: 5 }) +
        celula("13", "Sexo:", sexoTxt, { span: 3, obrig: true, opcoes: "1. Feminino 2. Masculino" })
    )}
    ${linha(
      celula("14", "Data de nascimento:", formatarDataBR(paciente.dataNascimento), { span: 3, obrig: true }) +
        celula("15", "Raça/Cor:", "", { span: 5, obrig: true, opcoes: "1. Branca 2. Preta 3. Amarela 4. Parda 5. Indígena 6. Não informado 7. Ignorada" }) +
        celula("16", "Etnia:", "", { span: 2, obrig: true }) +
        celula("17", "Nacionalidade:", "", { span: 2, obrig: true })
    )}
    ${linha(
      celula("18", "Número da identidade:", escapeHtml(paciente.rg), { span: 3 }) +
        celula("19", "Prontuário:", "", { span: 3 }) +
        celula("20", "Logradouro:", escapeHtml(paciente.logradouro), { span: 6, obrig: true })
    )}
    ${linha(
      celula("21", "Número:", escapeHtml(paciente.numero), { span: 2, obrig: true }) +
        celula("22", "Complemento:", escapeHtml(paciente.complemento), { span: 2 }) +
        celula("23", "Bairro:", escapeHtml(paciente.bairro), { span: 3, obrig: true }) +
        celula("24", "Município:", escapeHtml(paciente.cidade), { span: 3, obrig: true }) +
        celula("25", "Cód. IBGE:", "", { span: 2 })
    )}
    ${linha(
      celula("26", "UF:", escapeHtml(paciente.estado), { span: 2, obrig: true }) +
        celula("27", "CEP:", escapeHtml(paciente.cep), { span: 3, obrig: true }) +
        celula("28", "Telefone:", escapeHtml(telefoneVigente(paciente)), { span: 3 }) +
        celula("29", "País:", "", { span: 4, obrig: true })
    )}
    ${linha(
      celula("30", "Nome da mãe:", escapeHtml(paciente.nomeMae), { span: 8, obrig: true }) +
        celula("31", "CPF da mãe:", "", { span: 4, obrig: true })
    )}`;
}

// Campo 34 — Motivo da solicitação, em caixas de marcar (☑ na opção escolhida).
function motivoHIVCaixas(selecionado: string | undefined): string {
  const n = (selecionado || "").trim().charAt(0); // "1" | "2" | "3" | ""
  const cx = (digito: string, texto: string): string =>
    `<div class="cv-cx">${n === digito ? "☑" : "☐"} ${texto}</div>`;
  return `
    <div class="cv-campo cv-cx-bloco" style="grid-column: span 12;">
      <div class="cv-topo"><span class="cv-num">34</span><span class="cv-lbl">Motivo da solicitação<span class="cv-ob">*</span></span></div>
      ${cx("1", "Teste complementar após resultado de primeira carga viral de HIV detectável (qualquer valor).")}
      ${cx("2", "Teste complementar após primeira CV-HIV detectável (qualquer valor) e segunda CV-HIV inferior a 100 cópias/mL.")}
      ${cx("3", "Outro motivo (solicitar autorização prévia do DATHI/SVSA/MS — tratamento@aids.gov.br).")}
    </div>`;
}

// Corpo do formulário de DNA pró-viral do HIV-1.
function corpoCargaViralHIV(
  paciente: Paciente,
  solicitante: SolicitanteCargaViral,
  pedido: DadosCargaViral,
  config: ConfigCargaViral,
  selo?: string
): string {
  return `
  <div class="cv">
    ${cabecalhoEDadosHIV(paciente, solicitante, pedido, config, selo)}

    ${linha(
      celula("32", "Código do procedimento:", escapeHtml(config.codigoProcedimento), { span: 3 }) +
        celula("33", "Nome do procedimento:", escapeHtml(config.nomeProcedimento), { span: 9 })
    )}

    ${barra("DADOS DA SOLICITAÇÃO DO EXAME")}
    ${linha(motivoHIVCaixas(pedido.motivoExame))}

    ${barra("DADOS LABORATORIAIS — EVIDÊNCIA LABORATORIAL DE INFECÇÃO PELO HIV (CV-HIV)")}
    ${linha(
      celula("35", "Data da 1ª coleta:", escapeHtml(pedido.hivData1Coleta), { span: 3, obrig: true }) +
        celula("36", "Metodologia (1ª coleta):", escapeHtml(pedido.hivMetodologia1), { span: 3 }) +
        celula("37", "Nº de cópias/mL (1º exame):", escapeHtml(pedido.hivCopias1), { span: 3, obrig: true }) +
        celula("38", "Data do resultado (1º exame):", escapeHtml(pedido.hivDataResultado1), { span: 3, obrig: true })
    )}
    ${linha(
      celula("39", "Data da 2ª coleta:", escapeHtml(pedido.hivData2Coleta), { span: 3, obrig: true }) +
        celula("40", "Metodologia (2ª coleta):", escapeHtml(pedido.hivMetodologia2), { span: 3 }) +
        celula("41", "Nº de cópias/mL (2º exame):", escapeHtml(pedido.hivCopias2), { span: 3, obrig: true }) +
        celula("42", "Data do resultado (2º exame):", escapeHtml(pedido.hivDataResultado2), { span: 3, obrig: true })
    )}

    ${barra("LOCAL DA COLETA DA AMOSTRA")}
    ${linha(
      celula("43", "Nome da instituição (carimbo padrão):", "", { span: 6, obrig: true }) +
        celula("44", "Material biológico:", "Sangue total", { span: 2, obrig: true }) +
        celula("45", "Data da coleta:", escapeHtml(pedido.dataColeta), { span: 2, obrig: true }) +
        celula("46", "Hora da coleta:", "", { span: 2, obrig: true })
    )}

    ${barra("LOCAL DE RECOLHIMENTO DA AMOSTRA")}
    ${linha(
      celula("47", "Nome da instituição (carimbo padrão):", "", { span: 9, obrig: true }) +
        celula("48", "Data do recolhimento:", "", { span: 3, obrig: true })
    )}

    ${barra("LABORATÓRIO EXECUTOR DO TESTE")}
    ${linha(
      celula("49", "Nome da instituição (carimbo padrão):", "", { span: 6, obrig: true }) +
        celula("50", "CNES:", "", { span: 2 }) +
        celula("51", "Data do recebimento:", "", { span: 2, obrig: true }) +
        celula("52", "Hora do recebimento:", "", { span: 2, obrig: true })
    )}

    <div class="cv-rodape">* Preenchimento obrigatório · ${escapeHtml(config.fonte)} · Gerado por hepato.app — confira os dados antes de entregar ao laboratório.</div>
  </div>`;
}

// ------------------------------------------------------------
// GENOTIPAGEM DO VHC — gov.br/aids 08/02/2023
// ------------------------------------------------------------
// Formulário próprio: cabeçalho com Peso (campo 14) e Número GAL (campo 24);
// seção clínica com "Última quantificação do RNA do HCV" (campo 40), que é
// pré-preenchida com a sorologia mais recente do paciente (PedidoExames lê
// SorologiaViral.hcvRNA); e "solicitação/coleta simultânea" (40.1/44).

function cabecalhoEDadosGenoHCV(
  paciente: Paciente,
  solicitante: SolicitanteCargaViral,
  pedido: DadosCargaViral,
  config: ConfigCargaViral,
  // B289 (AI_127) — selo ICP-Brasil compacto no campo 7, só no PDF assinado.
  selo?: string
): string {
  const cnsPaciente = paciente.cns || paciente.hospub;
  const sexoTxt =
    paciente.sexo === "F"
      ? "1. Feminino"
      : paciente.sexo === "M"
      ? "2. Masculino"
      : "";
  const gestanteTxt = paciente.sexo === "M" ? "2. Não" : "";
  const documentoProf = solicitante.cpfProfissional
    ? `CPF: ${escapeHtml(solicitante.cpfProfissional)}`
    : "";

  return `
    <div class="cv-cab">
      <div class="cv-logo"><img src="${susLogoBase64}" alt="Logo do SUS" /></div>
      <div class="cv-titulo">${escapeHtml(config.titulo)}</div>
    </div>

    ${barra("DADOS DA INSTITUIÇÃO")}
    ${linha(
      celula("1", "Instituição solicitante (carimbo padrão):", escapeHtml(solicitante.unidadeSaude), { span: 9, obrig: true }) +
        celula("2", "CNES:", escapeHtml(solicitante.cnes), { span: 3, obrig: true })
    )}

    ${barra("DADOS DO PROFISSIONAL SOLICITANTE")}
    <div class="cv-linha cv-grid-inst">
      ${celula("3", "Nome do profissional solicitante:", escapeHtml(solicitante.nomeProfissional), { obrig: true, estilo: "grid-column: 1 / span 5; grid-row: 1;" })}
      ${celula("6", "Documento do profissional (CPF/CNS):", documentoProf, { obrig: true, estilo: "grid-column: 6 / span 4; grid-row: 1;" })}
      ${celula("4", "Data da solicitação:", escapeHtml(pedido.dataSolicitacao), { obrig: true, estilo: "grid-column: 1 / span 5; grid-row: 2;" })}
      ${celula("5", "Registro do conselho (Conselho/UF/Nº):", escapeHtml(solicitante.registroConselho), { obrig: true, estilo: "grid-column: 6 / span 4; grid-row: 2;" })}
      ${celula("7", "Assinatura e Carimbo:", selo || "", { obrig: true, estilo: "grid-column: 10 / span 3; grid-row: 1 / span 2;" })}
    </div>

    ${barra("INFORMAÇÕES BÁSICAS")}
    ${linha(
      celula("8", "CNS do(a) paciente:", escapeHtml(cnsPaciente), { span: 4, obrig: true }) +
        celula("10", "Nome completo do(a) usuário(a) — Oficial:", escapeHtml(paciente.nome), { span: 5, obrig: true }) +
        celula("12", "Preferência de identificação:", "1. Oficial", { span: 3, obrig: true, opcoes: "1. Oficial 2. Social" })
    )}
    ${linha(
      celula("9", "CPF:", escapeHtml(paciente.cpf), { span: 4, obrig: true }) +
        celula("11", "Nome social:", "", { span: 3 }) +
        celula("13", "Sexo:", sexoTxt, { span: 2, obrig: true, opcoes: "1. Fem 2. Masc" }) +
        celula("14", "Peso (kg) — se < 12 anos:", escapeHtml(pedido.pesoPaciente), { span: 3, obrig: true })
    )}
    ${linha(
      celula("15", "Data de nascimento:", formatarDataBR(paciente.dataNascimento), { span: 3, obrig: true }) +
        celula("16", "Raça/Cor:", "", { span: 4, obrig: true, opcoes: "1. Branca 2. Preta 3. Amarela 4. Parda 5. Indígena 6. Não informado 7. Ignorada" }) +
        celula("17", "Etnia:", "", { span: 2, obrig: true }) +
        celula("18", "Nome da mãe:", escapeHtml(paciente.nomeMae), { span: 3, obrig: true })
    )}
    ${linha(
      celula("19", "Nacionalidade:", "", { span: 3, obrig: true }) +
        celula("20", "Número da identidade:", escapeHtml(paciente.rg), { span: 2 }) +
        celula("21", "Logradouro:", escapeHtml(paciente.logradouro), { span: 4, obrig: true }) +
        celula("22", "Número:", escapeHtml(paciente.numero), { span: 2, obrig: true }) +
        celula("23", "Compl.:", escapeHtml(paciente.complemento), { span: 1 })
    )}
    ${linha(
      celula("24", "Número GAL:", escapeHtml(pedido.numeroGAL), { span: 2 }) +
        celula("25", "Bairro:", escapeHtml(paciente.bairro), { span: 2, obrig: true }) +
        celula("26", "Município:", escapeHtml(paciente.cidade), { span: 3, obrig: true }) +
        celula("27", "Cód. IBGE:", "", { span: 2 }) +
        celula("28", "UF:", escapeHtml(paciente.estado), { span: 1, obrig: true }) +
        celula("29", "CEP:", escapeHtml(paciente.cep), { span: 2, obrig: true })
    )}
    ${linha(
      celula("30", "Telefone:", escapeHtml(telefoneVigente(paciente)), { span: 3 }) +
        celula("31", "País:", "", { span: 2, obrig: true }) +
        celula("32", "Prontuário:", "", { span: 2 }) +
        celula("33", "Gestante:", gestanteTxt, { span: 2, obrig: true, opcoes: "1. Sim 2. Não" }) +
        celula("34", "Escolaridade (anos):", "", { span: 3, opcoes: "1. Nenhuma 2. 1 a 3 3. 4 a 7 4. 8 a 11 5. 12 e mais 6. Não inf. 7. Ignorado" })
    )}
    ${linha(
      celula("35", "Nome do(a) responsável (se menor de idade ou incapaz):", "", { span: 8, obrig: true }) +
        celula("36", "CPF do(a) responsável:", "", { span: 4 })
    )}`;
}

function corpoGenoHCV(
  paciente: Paciente,
  solicitante: SolicitanteCargaViral,
  pedido: DadosCargaViral,
  config: ConfigCargaViral,
  selo?: string
): string {
  return `
  <div class="cv">
    ${cabecalhoEDadosGenoHCV(paciente, solicitante, pedido, config, selo)}

    ${linha(
      celula("37", "Código do procedimento:", escapeHtml(config.codigoProcedimento), { span: 3 }) +
        celula("38", "Nome do procedimento:", escapeHtml(config.nomeProcedimento), { span: 9 })
    )}

    ${barra("DADOS CLÍNICOS GERAIS / DETALHES DO AGRAVO")}
    ${linha(
      celula("39", "CID10:", escapeHtml(pedido.genoCID || "B18.2"), { span: 2, obrig: true }) +
        celula("40", "Última quantificação do RNA do HCV — Data:", escapeHtml(pedido.genoUltimaQuantData), { span: 2, obrig: true }) +
        celula("", "UI/mL:", escapeHtml(pedido.genoUltimaQuantUIML), { span: 2 }) +
        celula("", "Log:", escapeHtml(pedido.genoUltimaQuantLog), { span: 1 }) +
        celula("40.1", "Solicitação simultânea de carga viral e genotipagem?", escapeHtml(pedido.genoSolicitacaoSimultanea), { span: 5, obrig: true, opcoes: "1. Sim 2. Não" })
    )}
    <div class="cv-nota">Campo 40: carga viral de pelo menos 500 UI/mL, realizada em até 12 meses (Nota Informativa nº 32/2016).</div>

    ${barra("LOCAL DA COLETA DA AMOSTRA")}
    ${linha(
      celula("41", "Nome da instituição (carimbo padrão):", "", { span: 6, obrig: true }) +
        celula("42", "Data da coleta:", escapeHtml(pedido.dataColeta), { span: 3, obrig: true }) +
        celula("43", "Hora da coleta:", "", { span: 3, obrig: true })
    )}
    ${linha(
      celula("44", "Coleta simultânea de amostras de carga viral e genotipagem?", escapeHtml(pedido.genoColetaSimultanea), { span: 12, obrig: true, opcoes: "1. Sim 2. Não" })
    )}

    ${barra("LABORATÓRIO EXECUTOR DO TESTE")}
    ${linha(
      celula("45", "Nome da instituição (carimbo padrão):", "", { span: 6, obrig: true }) +
        celula("46", "CNES:", "", { span: 2, obrig: true }) +
        celula("47", "Data do recebimento:", "", { span: 2, obrig: true }) +
        celula("48", "Hora do recebimento:", "", { span: 2, obrig: true })
    )}
    ${linha(
      celula("49", "Genótipo:", "", { span: 4 }) +
        celula("50", "Subtipo:", "", { span: 4 }) +
        celula("51", "Metodologia utilizada:", "", { span: 4 })
    )}

    <div class="cv-rodape">* Preenchimento obrigatório · ${escapeHtml(config.fonte)} · Gerado por hepato.app — confira os dados antes de entregar ao laboratório.</div>
  </div>`;
}

// CSS estrutural (sem @page/body), reusável no documento autônomo ou anexado.
const ESTILOS_CARGA_VIRAL = `
    .cv { background: #fff; padding: 4mm; }
    /* CABEÇALHO */
    .cv-cab { display: flex; align-items: center; gap: 12px; border: 1.2px solid #000; padding: 4px 10px; margin-bottom: 0; }
    .cv-logo { flex: 0 0 auto; }
    .cv-logo img { width: 72px; height: auto; display: block; }
    .cv-titulo { font-weight: bold; font-size: 13px; line-height: 1.15; }
    /* BARRAS DE SEÇÃO (horizontais) */
    .cv-barra {
      background: #c9c9c9; border-left: 1.2px solid #000; border-right: 1.2px solid #000;
      border-bottom: 1px solid #000; padding: 1px 6px;
    }
    .cv-barra span { font-weight: bold; font-size: 9px; letter-spacing: .5px; }
    /* GRADE */
    .cv-linha {
      display: grid; grid-template-columns: repeat(12, 1fr);
      border-left: 1.2px solid #000; border-right: 1.2px solid #000;
    }
    .cv-campo {
      border-right: 1px solid #000; border-bottom: 1px solid #000;
      padding: 1px 3px; min-width: 0; display: flex; flex-direction: column;
      min-height: 30px;
    }
    .cv-linha > .cv-campo:last-child { border-right: none; }
    .cv-alto { min-height: 40px; }
    .cv-topo { display: flex; align-items: baseline; gap: 3px; }
    .cv-num {
      display: inline-block; border: 1px solid #000; font-size: 7px; line-height: 1;
      padding: 1px 2px; font-weight: bold; flex: 0 0 auto;
    }
    .cv-lbl { font-size: 8px; font-weight: normal; line-height: 1.1; }
    .cv-ob { font-weight: bold; }
    .cv-opts { font-size: 6.5px; color: #222; line-height: 1.12; margin-top: 1px; }
    .cv-fill {
      margin-top: 1px; min-height: 13px; flex: 1;
      background: linear-gradient(#e6e6e6, #fbfbfb);
      font-size: 12px; font-weight: 600; padding: 1px 3px; overflow: hidden;
    }
    /* SEÇÃO COM FAIXA VERTICAL "CARGA VIRAL" */
    .cv-secao-band { display: flex; border-left: 1.2px solid #000; border-right: 1.2px solid #000; }
    .cv-band {
      width: 16px; flex: 0 0 16px; background: #ededed; border-right: 1px solid #000;
      display: flex; align-items: center; justify-content: center;
    }
    .cv-band span {
      writing-mode: vertical-rl; transform: rotate(180deg);
      font-weight: bold; font-size: 8px; letter-spacing: 1px; white-space: nowrap;
    }
    .cv-band-corpo { flex: 1; min-width: 0; }
    .cv-band-corpo .cv-linha { border-left: none; border-right: none; }
    .cv-rodape {
      border: 1.2px solid #000; border-top: none; font-size: 7px; color: #333;
      padding: 2px 6px; text-align: left;
    }
    /* HIV — caixas de marcar do "Motivo da solicitação" (campo 34) */
    .cv-cx-bloco { gap: 1px; }
    .cv-cx { font-size: 8px; line-height: 1.25; padding: 1px 2px; }
    /* Genotipagem — nota explicativa do campo 40 */
    .cv-nota {
      border-left: 1.2px solid #000; border-right: 1.2px solid #000;
      border-bottom: 1px solid #000; font-size: 6.5px; color: #444;
      padding: 1px 6px; font-style: italic;
    }
`;

// ------------------------------------------------------------
// PONTOS DE ENTRADA
// ------------------------------------------------------------
// Escolhe o corpo conforme a variante da config (Delta tem layout próprio).
function corpoPorVariante(
  paciente: Paciente,
  solicitante: SolicitanteCargaViral,
  pedido: DadosCargaViral,
  config: ConfigCargaViral,
  // B289 (AI_127) — selo ICP-Brasil compacto na assinatura do solicitante.
  selo?: string
): string {
  if (config.variante === "delta")
    return corpoCargaViralDelta(paciente, solicitante, pedido, config, selo);
  if (config.variante === "hiv")
    return corpoCargaViralHIV(paciente, solicitante, pedido, config, selo);
  if (config.variante === "genoHcv")
    return corpoGenoHCV(paciente, solicitante, pedido, config, selo);
  return corpoCargaViral(paciente, solicitante, pedido, config, selo);
}

/**
 * Fragmento para ANEXAR ao pedido de exames comum (mesmo documento de
 * impressão). Devolve estilos (escopados em classes `.cv-*`) e o corpo numa
 * folha com página nomeada `cargaviral` (margem 5mm) e quebra de página antes.
 */
export function montarFragmentoCargaViral(
  paciente: Paciente,
  solicitante: SolicitanteCargaViral,
  pedido: DadosCargaViral,
  config: ConfigCargaViral,
  // B289 (AI_127) — selo ICP-Brasil compacto na assinatura, só no PDF assinado.
  selo?: string
): { estilos: string; corpo: string } {
  const estilos = `
    @page cargaviral { size: A4 portrait; margin: 5mm; }
    .folha-cv { page: cargaviral; page-break-before: always; }
    ${ESTILOS_CARGA_VIRAL}
    .folha-cv .cv { width: auto; margin: 0; padding: 0; }
  `;
  const corpo = `<div class="folha-cv">${corpoPorVariante(
    paciente,
    solicitante,
    pedido,
    config,
    selo
  )}</div>`;
  return { estilos, corpo };
}

/** Documento AUTÔNOMO da ficha de carga viral (sem pedido comum). */
function montarHTML(
  paciente: Paciente,
  solicitante: SolicitanteCargaViral,
  pedido: DadosCargaViral,
  config: ConfigCargaViral
): string {
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8" />
  <title>${escapeHtml(config.titulo)} — ${escapeHtml(paciente.nome)}</title>
  <style>
    @page { size: A4 portrait; margin: 5mm; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Arial, Helvetica, sans-serif; color: #000; margin: 0; background: #e9e9e9; }
    .barra-botoes { text-align: center; padding: 14px; }
    .cv { width: 200mm; margin: 0 auto 20px; }
    ${ESTILOS_CARGA_VIRAL}
    @media print {
      body { background: #fff; }
      .cv { width: auto; margin: 0; padding: 0; }
      .barra-botoes { display: none; }
    }
  </style>
</head>
<body>
  <div class="barra-botoes">
    <button onclick="window.print()" style="padding: 10px 32px; background: #2563eb; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; margin-right: 8px;">
      🖨️ Imprimir / Salvar PDF
    </button>
    <button onclick="window.close()" style="padding: 10px 32px; background: #6b7280; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;">
      ✕ Fechar
    </button>
  </div>
  ${corpoPorVariante(paciente, solicitante, pedido, config)}
</body>
</html>`;
}

export function gerarFichaCargaViral(
  paciente: Paciente,
  solicitante: SolicitanteCargaViral,
  pedido: DadosCargaViral,
  config: ConfigCargaViral = CONFIG_HBV
): void {
  const html = montarHTML(paciente, solicitante, pedido, config);
  abrirPDFEmNovaJanela(html);
}
